import { Star, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUser } from "@supabase/auth-helpers-react";
import {
  checkTutorialCompletion,
  setTutorialRating,
  getUserTutorialRating,
  deleteTutorialRating,
} from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { ReactNode, useEffect, useState } from "react";
import { CardContainer } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/ui/use-toast";
import { useTutorials } from "@/hooks/useTutorials";
import { Link } from "react-router";
import { useCategoryStore } from "@/stores/use-tutorial-progress";

interface TutorialCompletionProps {
  variant?: "full" | "buttonOnly";
  documentId: string;
  isCompleted?: boolean;
  onComplete?: () => void;
  tutorialButtons?: ReactNode;
}

export const TutorialCompletion = ({
  variant = "full",
  documentId,
  isCompleted: propIsCompleted = false,
  onComplete,
  tutorialButtons,
}: TutorialCompletionProps) => {
  const user = useUser();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { updateCompletionStatus } = useTutorials({});
  const [isCompleted, setIsCompleted] = useState(propIsCompleted);
  const [userRating, setUserRating] = useState<number | null>(null);
  const [isRatingLoading, setIsRatingLoading] = useState(false);
  const [isCompletionLoading, setIsCompletionLoading] = useState(false);
  const { updateTutorialCompletion } = useCategoryStore();
  useEffect(() => {
    const checkCompletion = async () => {
      if (!user?.id || !documentId) return;

      try {
        const completed = await checkTutorialCompletion(user.id, documentId);
        setIsCompleted(completed);
      } catch (error: unknown) {
        console.error("Error checking tutorial completion:", error);
        toast({
          variant: "destructive",
          title: t("error"),
          description: t("common.genericError"),
        });
      }
    };

    checkCompletion();
  }, [user?.id, documentId, t, toast]);

  useEffect(() => {
    const loadUserRating = async () => {
      if (!user?.id || !documentId) return;

      try {
        const rating = await getUserTutorialRating(user.id, documentId);
        setUserRating(rating);
      } catch (error: unknown) {
        console.error("Error loading user rating:", error);
        toast({
          variant: "destructive",
          title: t("error"),
          description: t("common.genericError"),
        });
      }
    };

    loadUserRating();
  }, [user?.id, documentId, t, toast]);

  const handleComplete = async () => {
    if (!user?.id || !documentId) {
      console.log("Missing user or documentId", {
        userId: user?.id,
        documentId,
      });
      return;
    }

    setIsCompletionLoading(true);
    try {
      // Use the updateCompletionStatus function from useTutorials hook
      // This will update the completedIds array and invalidate the cache
      const newCompletionStatus = !isCompleted;
      await updateCompletionStatus(documentId, newCompletionStatus);
      updateTutorialCompletion(documentId, newCompletionStatus);
      setIsCompleted(newCompletionStatus);
      onComplete?.();
    } catch (error: unknown) {
      console.error("Error toggling tutorial completion:", error);
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("common.genericError"),
      });
      // Reset state to original value on error
      setIsCompleted(!isCompleted);
    } finally {
      setIsCompletionLoading(false);
    }
  };

  const handleRating = async (rating: number) => {
    if (!user?.id || !documentId) return;

    setIsRatingLoading(true);
    try {
      await setTutorialRating(user.id, documentId, rating);
      setUserRating(rating);
    } catch (error: unknown) {
      console.error("Error setting tutorial rating:", error);
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("common.genericError"),
      });
      // Revert the optimistic update on error
      const currentRating = await getUserTutorialRating(user.id, documentId);
      setUserRating(currentRating);
    } finally {
      setIsRatingLoading(false);
    }
  };

  const handleClearRating = async () => {
    if (!user?.id || !documentId) return;

    setIsRatingLoading(true);
    try {
      await deleteTutorialRating(user.id, documentId);
      setUserRating(null);
    } catch (error: unknown) {
      console.error("Error clearing tutorial rating:", error);
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("common.genericError"),
      });
      // Revert the optimistic update on error
      const currentRating = await getUserTutorialRating(user.id, documentId);
      setUserRating(currentRating);
    } finally {
      setIsRatingLoading(false);
    }
  };

  const renderBackToFeedButton = () => (
    <Button
      asChild
      size="sm"
      variant="outline"
      className={cn(
        "transition-colors duration-200 text-foreground",
        variant === "full" ? "w-full sm:w-auto" : ""
      )}
    >
      <Link to="/">{t("tutorial.navigation.backToFeed", "Back to Feed")}</Link>
    </Button>
  );

  const renderButton = () => (
    <Button
      onClick={handleComplete}
      disabled={isCompletionLoading}
      size="sm"
      className={cn(
        "transition-colors duration-200",
        isCompleted
          ? "bg-green-500 hover:bg-green-600 text-white"
          : "bg-primary text-primary-foreground hover:bg-primary/90",
        variant === "full" ? "w-full sm:w-auto" : ""
      )}
    >
      {isCompletionLoading
        ? t("tutorial.completion.loading")
        : isCompleted
        ? t("tutorial.completion.completed")
        : t("tutorial.completion.markComplete")}
    </Button>
  );

  if (variant === "buttonOnly") {
    return (
      <div className="flex gap-2">
        {renderBackToFeedButton()}
        {renderButton()}
      </div>
    );
  }

  return (
    <CardContainer className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          {t("tutorial.completion.rateThis")}
        </span>
        <div className="flex items-center gap-1">
          <div className="flex gap-1">
            {[1, 2, 3].map((rating) => (
              <Star
                key={rating}
                className={cn(
                  "h-5 w-5 cursor-pointer transition-colors duration-200",
                  isRatingLoading && "cursor-wait opacity-50",
                  rating <= (userRating || 0)
                    ? "text-warning fill-warning"
                    : "text-muted hover:text-warning/50"
                )}
                onClick={() => !isRatingLoading && handleRating(rating)}
              />
            ))}
          </div>
          {userRating !== null && (
            <X
              className={cn(
                "h-4 w-4 cursor-pointer text-muted-foreground hover:text-destructive transition-colors duration-200 ml-1",
                isRatingLoading && "cursor-wait opacity-50"
              )}
              onClick={() => !isRatingLoading && handleClearRating()}
            />
          )}
        </div>
        <div className="">{tutorialButtons}</div>
      </div>
      <div className="flex gap-2 flex-wrap sm:flex-nowrap">
        {renderBackToFeedButton()}
        {renderButton()}
      </div>
    </CardContainer>
  );
};
